import { StateCreator } from 'zustand';
import { DocumentMode } from 'types/Document';
import { BoundStoreState, useBoundStore } from './index';
import { SnackbarProps, Mode, LayoutSliceState } from './types';

export const createLayoutSlice: StateCreator<
		BoundStoreState,
		[],
		[],
		LayoutSliceState
> = () => ({
  snackbarState: {
    open: false,
    message: '',
    severity: 'info',
    autoHideDuration: 5000,
  },
  themeMode: 'light',
  isLightMode: true,
  sharedPopupOpen: false,
  showFAQ: false,
  layoutTempState: {},
  isMobile: false,
  documentMode: DocumentMode.Read,
  showFilters: false,
  showDocumentSidebar: true,
});

export const setSnackbarState = (snackbarState: SnackbarProps) => {
  useBoundStore.setState({ snackbarState });
};

export const closeSnackbar = () => {
  const { snackbarState } = useBoundStore.getState();
  useBoundStore.setState({
    snackbarState: {
      ...snackbarState,
      open: false,
    },
  });
};

export const changesSaved = (dictionary: any) => {
  useBoundStore.setState({
    snackbarState: {
      open: true,
      message: dictionary.changesSavedSuccessfully,
      severity: 'success',
      autoHideDuration: 2000,
    },
  });
};

export const validationError = (dictionary: any, message?: string) => {
  useBoundStore.setState({
    snackbarState: {
      open: true,
      message: message || dictionary.oneOrMoreFieldsAreIncorrect,
      severity: 'error',
      autoHideDuration: 3000,
    },
  });
};

export const genericError = (dictionary: any) => {
  useBoundStore.setState({
    snackbarState: {
      open: true,
      message: dictionary.somethingWentWrong,
      severity: 'error',
      autoHideDuration: 2000,
    },
  });
};

export const genericFeedback = (message: string = '', duration = 2000) => {
  useBoundStore.setState({
    snackbarState: {
      open: true,
      message: message,
      severity: 'success',
      autoHideDuration: duration,
    },
  });
};

export const sendNotification = (message: string) => {
  useBoundStore.setState({
    snackbarState: {
      open: true,
      message,
      severity: 'success',
      autoHideDuration: 5000,
    },
  });
};

export const toggleMode = () => {
  const { themeMode } = useBoundStore.getState();
  const newMode = themeMode === 'dark' ? 'light' : 'dark';
  setThemeMode(newMode);
};

export const setThemeMode = (themeMode: Mode) => {
  localStorage.setItem('mode', themeMode);
  useBoundStore.setState({
    themeMode,
    isLightMode: themeMode === 'light',
  });
};

export const setSharedPopupOpen = (sharedPopupOpen: boolean) => {
  useBoundStore.setState({ sharedPopupOpen });
};

export const setShowFAQ = (showFAQ: boolean) => {
  useBoundStore.setState({ showFAQ });
};

export const setLayoutTempState = (layoutTempState: any) => {
  useBoundStore.setState({ layoutTempState });
};

export const setIsMobile = (isMobile: boolean) => {
  useBoundStore.setState({ isMobile });
};

export const setDocumentMode = (documentMode: DocumentMode) => {
  useBoundStore.setState({ documentMode });
};

export const setShowFilters = (showFilters: boolean) => {
  useBoundStore.setState({ showFilters });
};

export const setShowDocumentSidebar = (showDocumentSidebar: boolean) => {
  useBoundStore.setState({ showDocumentSidebar });
};

export const useSnackbarState = () => useBoundStore((state) => state.snackbarState);
export const useThemeMode = () => useBoundStore((state) => state.themeMode);
export const useIsLightMode = () => useBoundStore((state) => state.isLightMode);
export const useSharedPopupOpen = () => useBoundStore((state) => state.sharedPopupOpen);
export const useShowFAQ = () => useBoundStore((state) => state.showFAQ);
export const useLayoutTempState = () => useBoundStore((state) => state.layoutTempState);
export const useIsMobile = () => useBoundStore((state) => state.isMobile);
export const useDocumentMode = () => useBoundStore((state) => state.documentMode);
export const useShowFilters = () => useBoundStore((state) => state.showFilters);
export const useShowDocumentSidebar = () => useBoundStore((state) => state.showDocumentSidebar);

export const useReadMode = () => useBoundStore((state) => state.documentMode === DocumentMode.Read);
export const useWriteMode = () => useBoundStore((state) => state.documentMode === DocumentMode.Write);
export const useReviewMode = () => useBoundStore((state) => state.documentMode === DocumentMode.Review);

export const useLayoutContext = () => {
  const snackbarState = useSnackbarState();
  const themeMode = useThemeMode();
  const isLightMode = useIsLightMode();
  const sharedPopupOpen = useSharedPopupOpen();
  const showFAQ = useShowFAQ();
  const layoutTempState = useLayoutTempState();
  const isMobile = useIsMobile();
  const documentMode = useDocumentMode();
  const showFilters = useShowFilters();
  const showDocumentSidebar = useShowDocumentSidebar();
  const readMode = useReadMode();
  const writeMode = useWriteMode();
  const reviewMode = useReviewMode();

  return {
    snackbarState,
    setSnackbarState,
    closeSnackbar,
    changesSaved,
    validationError,
    genericError,
    genericFeedback,
    sendNotification,
    themeMode,
    mode: themeMode,
    setMode: setThemeMode,
    toggleMode: toggleMode,
    isLightMode,
    sharedPopupOpen,
    setSharedPopupOpen,
    showFAQ,
    setShowFAQ,
    layoutTempState,
    setLayoutTempState,
    isMobile,
    documentMode,
    setDocumentMode,
    showFilters,
    setShowFilters,
    showDocumentSidebar,
    setShowDocumentSidebar,
    readMode,
    writeMode,
    reviewMode,
  };
};